import {
  type ActiveScope,
  type RolodexV3UserScope,
  rolodexV3UserScopeSchema,
  ROOT_SCOPE_NAME,
  ROOT_SCOPE_TYPE,
  scopeCovers,
} from 'features/permissions/utils/policyScopeEvaluation'
import { z } from 'zod'

export interface RolodexV3ScopedRole {
  name: string
  scopes: readonly RolodexV3UserScope[]
}

const UNSCOPED_ROLE_SCOPES: readonly RolodexV3UserScope[] = [
  { type: ROOT_SCOPE_TYPE, name: ROOT_SCOPE_NAME },
]

const namedRoleSchema = z.object({ name: z.string().min(1) })
const v3GetRoleSchema = z.union([
  namedRoleSchema,
  z.object({
    role: z.union([z.string().min(1), namedRoleSchema]),
    scopes: z.array(rolodexV3UserScopeSchema).optional(),
  }),
])

function scopedRoleFromItem(
  item: z.infer<typeof v3GetRoleSchema>
): RolodexV3ScopedRole {
  if ('role' in item) {
    const name = typeof item.role === 'string' ? item.role : item.role.name
    const scopes = item.scopes?.length ? item.scopes : UNSCOPED_ROLE_SCOPES
    return { name, scopes }
  }
  return { name: item.name, scopes: UNSCOPED_ROLE_SCOPES }
}

/**
 * GET /users/{id}/roles is UserRole[] (`{ role, scopes }`) or Role[].
 * Rows without scopes are treated as root-scoped, matching how Rolodex
 * reads empty tags.
 */
export function v3ScopedRolesFromResponse(
  data: unknown
): RolodexV3ScopedRole[] {
  const parsed = z.array(v3GetRoleSchema).safeParse(data)
  if (!parsed.success) {
    throw new Error(
      `Failed to parse Rolodex v3 user roles: ${parsed.error.message}`
    )
  }
  return parsed.data.map(scopedRoleFromItem)
}

/** Names of the roles with a grant that covers the active scope, each once. */
export function v3RoleNamesForScope(
  roles: readonly RolodexV3ScopedRole[],
  active: ActiveScope
): string[] {
  const names = roles
    .filter((role) => role.scopes.some((scope) => scopeCovers(scope, active)))
    .map((role) => role.name)
  return [...new Set(names)]
}
